import { ErrorBoundary } from 'components/common/error-boundary';
import {
  VideoPlayerType,
  getPlaybackRateCookieKey,
  getPlaybackRateDefault,
} from 'enums/video-player.enums';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { IVideoPlayback } from 'lib_ts/interfaces/i-video';
import { isNil } from 'lodash';
import React from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './player.scss';

const FF_NAME_VIDEO_PLAYER_SPEED_CONTROL = 'video-player-speed-control';

interface IPropsHoC extends videojs.PlayerOptions {
  type: VideoPlayerType;

  /** contains playback details (e.g. poster URL, media URL, file type, etc...) */
  playback: IVideoPlayback;

  /** have player skip to this timestamp after setup */
  startAtTime?: number;

  /** called whenever player's timeupdate triggers, if player is paused */
  onPausedUpdate?: (currentTimeSec: number, durationSec: number) => void;

  // called whenever the player is paused/resumed
  onTogglePlayback?: (paused: boolean) => void;

  /** called whenever player's timeupdate triggers */
  onContinuousUpdate?: (
    currentTimeSec: number,
    durationSec: number,
    isPaused: boolean
  ) => void;
}

// this is a shim to get FF working
export const CommonVideoPlayerHoC = (props: IPropsHoC) => {
  const showControls = useFeatureFlag(FF_NAME_VIDEO_PLAYER_SPEED_CONTROL);
  return <CommonVideoPlayer {...props} showControls={showControls} />;
};

interface IPropsBase extends IPropsHoC {
  // this is a shim to get FF working w/o rewriting CommonVideoPlayer as fnl
  showControls: boolean;
}

interface IState {
  width: number;
  height: number;

  /** store the last video URL that has been set as player src */
  currentVideoURL?: string;
}

class CommonVideoPlayer extends React.Component<IPropsBase, IState> {
  private wrapperNode?: HTMLDivElement;
  private videoNode?: HTMLVideoElement;
  private player?: videojs.Player;
  private setupHook?: (player: videojs.Player) => void;
  private resizeTimer?: ReturnType<typeof setTimeout>;

  constructor(props: IPropsBase) {
    super(props);

    this.state = {
      width: 100,
      height: 100,
    };

    this.handleResize = this.handleResize.bind(this);
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleTogglePlayback = this.handleTogglePlayback.bind(this);
    this.setupPlayer = this.setupPlayer.bind(this);
  }

  componentDidMount() {
    /** listen for subsequent resize events to re-run resizer */
    window.addEventListener('resize', this.handleResize);

    /** resize video now that wrapper has spawned */
    this.handleResize();

    videojs.hook('setup', (player) => {
      /** should be checking against undefined because 0 is a valid value */
      if (!isNil(this.props.startAtTime)) {
        player.pause();
        player.currentTime(this.props.startAtTime);
      }
    });
  }

  /** needed for changing video without remounting component */
  componentDidUpdate() {
    if (this.state.currentVideoURL !== this.props.playback.video.url) {
      this.setupPlayer();
    }

    if (this.player && !isNil(this.props.startAtTime)) {
      if (this.player.currentTime() !== this.props.startAtTime) {
        this.player.currentTime(this.props.startAtTime);
      }
    }
  }

  // destroy player on unmount
  componentWillUnmount() {
    /** stop listening for resize events */
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimer);

    if (this.setupHook) {
      videojs.removeHook('setup', this.setupHook as () => any);
    }

    if (this.player) {
      // remove all event listeners set by this.player.on(...) to avoid duplicate listeners
      this.player.dispose();
    }
  }

  private handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this.wrapperNode) {
        return;
      }

      const aspectRatio =
        this.props.playback.video.cap_size_0 === undefined ||
        this.props.playback.video.cap_size_1 === undefined
          ? 10 / 16
          : this.props.playback.video.cap_size_0 /
            (this.props.playback.video.cap_size_1 || 1);

      const width = this.wrapperNode.offsetWidth ?? 100;
      const height = Math.round(width * aspectRatio);

      this.setState({
        width,
        height,
      });
    }, 100);
  }

  private setupPlayer() {
    if (!this.props.playback?.video?.url?.trim()) {
      return;
    }

    if (this.player) {
      /** update the src */
      this.player.src({
        type: this.props.playback.video.mime_type,
        src: this.props.playback.video.url,
      });

      this.setState({
        currentVideoURL: this.props.playback.video.url,
      });

      this.player.poster(this.props.playback.thumb.url);
      return;
    }

    /** instantiate Video.js */
    const options: videojs.PlayerOptions = {
      autoplay: !!this.props.autoplay,
      controls: true,
      sources: [
        {
          src: this.props.playback.video.url,
          type: this.props.playback.video.mime_type,
        },
      ],
      poster: this.props.playback.thumb.url,
      preload: 'auto', //starts loading if browser supports it

      playbackRates: this.props.showControls
        ? (this.props.playbackRates ?? [0.25, 0.5, 1, 1.5, 2])
        : undefined,
    };

    this.player = videojs(this.videoNode as HTMLVideoElement, options, () => {
      // nothing
    });

    if (this.props.showControls) {
      // * NOTE: 3rd party browser extensions to change playback speed can interfere with speed changes via the player
      // listen for playback rate changes and save to cookie
      this.player.on('ratechange', () => {
        if (!this.player) {
          // shouldn't trigger
          return;
        }

        const playbackRate =
          // this.player.tech(true)?.el()?.playbackRate ?? // ? if necessary, read the value from the video element directly, if the player's playbackRate() is not always updated immediately
          this.player.playbackRate();

        const key = getPlaybackRateCookieKey(
          this.props.type,
          this.props.playback.video.speed
        );

        localStorage.setItem(key, playbackRate.toString());
      });

      // * NOTE: 3rd party browser extensions to change playback speed can interfere with restoring saved speed changes
      // reapply user-defined playback rate if it exists in cookie
      this.player.on('loadstart', () => {
        if (!this.player) {
          // shouldn't trigger
          return;
        }

        // read from cookie and reapply playbackRate if it exists
        const key = getPlaybackRateCookieKey(
          this.props.type,
          this.props.playback.video.speed
        );

        const playbackRate = localStorage.getItem(key);

        const safePlaybackRate = playbackRate
          ? Number(playbackRate)
          : getPlaybackRateDefault(
              this.props.type,
              this.props.playback.video.speed
            );

        // this.player.defaultPlaybackRate(Number(safePlaybackRate)); // ? if necessary, set the default playback rate to the value from the cookie so it's not reset to 1 when the video is reloaded

        this.player.playbackRate(safePlaybackRate);
      });
    }

    this.player.on('timeupdate', this.handleTimeUpdate);

    this.player.on('pause', this.handleTogglePlayback);
    this.player.on('play', this.handleTogglePlayback);

    if (this.props.startAtTime !== undefined) {
      this.player.addClass('vjs-has-started');
    }

    this.setState({
      currentVideoURL: this.props.playback.video.url,
    });
  }

  private handleTimeUpdate() {
    /** only when player is ready */
    if (!this.player) {
      return;
    }

    /** regardless of if player is paused */
    this.props.onContinuousUpdate?.(
      this.player.currentTime(),
      this.player.duration(),
      this.player.paused()
    );

    /** only when player is paused */
    if (this.player.paused()) {
      this.props.onPausedUpdate?.(
        this.player.currentTime(),
        this.player.duration()
      );
    }
  }

  private handleTogglePlayback() {
    /** only when player is ready */
    if (!this.player) {
      return;
    }

    this.props.onTogglePlayback?.(this.player.paused());
  }

  render() {
    return (
      <ErrorBoundary identifier="CommonVideoPlayer">
        <div
          ref={(elem) => (this.wrapperNode = elem as HTMLDivElement)}
          style={{
            overflow: 'hidden',
            borderRadius: 'var(--radius-4)',
          }}
        >
          <div
            data-vjs-player
            style={{
              width: this.state.width,
              height: this.state.height,
            }}
          >
            <video
              ref={(elem) => (this.videoNode = elem as HTMLVideoElement)}
              className="video-js"
            />
          </div>
        </div>
      </ErrorBoundary>
    );
  }
}
